import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { VscSettingsGear } from 'react-icons/vsc';
import { useSelector } from 'react-redux';

import Loading from '../../../components/Loading';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import Button from '../../../components/Button/Button';
import { FormField } from '../../../components/FormField';
import { CustomSelect } from '../../../components/Shared/CustomSelect';
import { InputSearch } from '../../../components/InputSearch';
import { RoundedBox } from '../../../components/Shared/RoundedBox';
import { useGetStore, useGetStoreInventory } from '../../../network';
import { InventoryItems } from './InventoryItems';
import { useSetFilters } from '../../../hooks/useSetFilters';
import { FixedLoader } from '../../../components/FixedLoader';

export const Inventory = () => {
    const setFilters = useSetFilters();
    const { slug } = useParams();
    const { t } = useTranslation();

    const searchParams = new URLSearchParams(location.search);
    const categoryId = parseInt(searchParams.get('category')) || '';
    const menuId = searchParams.get('menu') || '';
    const search = (searchParams.get('search') || '').toLowerCase();

    const isInventoryLoading = useSelector((state) => state.inventory.loading);

    const { data: inventoryData = {}, isLoading } = useGetStoreInventory({ slug });
    const { data: store = {} } = useGetStore({ slug });

    const pages = [
        { name: t('Stores'), path: '/chain/stores' },
        { name: store.name, path: `/chain/stores/${slug}` },

        { name: t('Inventory'), path: `/chain/stores/${slug}/inventory` },
    ];

    // options for filter by menu
    const inventoryMenus = inventoryData.inventory?.map((menu) => ({ label: menu.name, value: menu.id })) || [];
    inventoryMenus.unshift({ label: t('All menus'), value: '' });

    // filtered inventory menu by menu id
    const filteredMenu = menuId ? inventoryData.inventory?.filter((menu) => menu.id === menuId) : inventoryData.inventory;

    // inventory categories
    const categories = filteredMenu?.flatMap((menu) => ([...menu.categories]|| []));

    // options for filtering by categories
    const inventoryCategories = [{ value: '', label: t('All Categories') }]

    // inventory dishes
    const dishes = categories?.flatMap((category) => {
        inventoryCategories.push({ label: category.name, value: category.id });
        return [...category.dishes];
    }) || [];

    const onChangeCategory = ({ value }) => setFilters({ category: value, page: 1 });
    const onChangeMenu = ({ value }) => setFilters({ menu: value, category: '', page: 1 });
    const onSearch = (e) => setFilters({ search: e.target.value });

    const defaultMenuOptions = inventoryMenus.find(menu => menu.value === menuId) || null
    const defaultCategoryOption = inventoryCategories.find(cat => cat.value === categoryId) || null

    return (
        <Loading loading={isLoading}>
            <div className="max-w-6xl">
                <Breadcrumbs pages={pages} />
                <div className="flex items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <div>
                            <Button isUpperCase={false} type="link" to={`/chain/stores/${slug}/inventory`} fontWeight="font-normal" tracing="tracking-normal" className="h-9 rounded-r-none px-14 font-bold">
                                {t('Store Items')}
                            </Button>
                            <Button
                                isUpperCase={false}
                                type="link"
                                to={`/chain/stores/${slug}/inventory/modifiers`}
                                fontWeight="font-normal"
                                tracing="tracking-normal"
                                color="bg-gray-300"
                                className="h-9 rounded-l-none border-l-2 px-14 font-bold pointer-events-none"
                            >
                                {t('Modifiers')}
                            </Button>
                        </div>
                        <VscSettingsGear className="mr-3 cursor-pointer" size={28} />
                    </div>
                </div>
                <div className="flex gap-6 items-center  mt-6">
                    <FormField>
                        <CustomSelect className="text-left w-40" options={inventoryMenus} value={defaultMenuOptions} onChange={onChangeMenu} />
                    </FormField>
                    <FormField>
                        <CustomSelect className="text-left w-40" options={inventoryCategories} value={defaultCategoryOption} onChange={onChangeCategory} />
                    </FormField>
                    <InputSearch defaultValue={search} onChange={onSearch} />
                </div>
                <RoundedBox className="mt-5">
                    <InventoryItems inventory={dishes} statuses={inventoryData.statuses} />
                </RoundedBox>
            </div>
            {isInventoryLoading && <FixedLoader />}
        </Loading>
    );
};
